import axios from 'axios';
import { getKioskToken, isKioskMode } from '../utils/kioskMode';

// Environment configuration
export const ENV = {
  current: process.env.REACT_APP_ENV || (process.env.NODE_ENV === 'production' ? 'production' : 'development'),
  isDevelopment: process.env.NODE_ENV !== 'production',
  isProduction: process.env.REACT_APP_ENV === 'production' || (process.env.NODE_ENV === 'production' && !process.env.REACT_APP_ENV),
};

// API configuration
export const API_CONFIG = {
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:3000',
  timeout: 30000,
};

// Create axios instance with environment-specific configuration
export const api = axios.create({
  baseURL: API_CONFIG.baseURL,
  timeout: API_CONFIG.timeout,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Attach session token from current session on every request
api.interceptors.request.use((config) => {
  // Kiosk mode: forward the URL-bound token as a header on every API call
  // and skip the localStorage / E2E paths entirely.
  if (isKioskMode()) {
    const kioskToken = getKioskToken();
    if (kioskToken) {
      config.headers['X-Kiosk-Token'] = kioskToken;
    }
    return config;
  }

  // E2E auth bypass — attach the bypass header when the build was configured
  // for it. Backend only honors this in non-prod envs with E2E_LOGIN_ENABLED=true.
  // See docs/WIP/e2e-auth-bypass.md.
  if (process.env.REACT_APP_E2E_LOGIN === 'true') {
    config.headers['X-E2E-User-Email'] =
      process.env.REACT_APP_E2E_USER_EMAIL || 'qa@hexacontainment.com';
  }

  const stored = localStorage.getItem('auth_user');
  if (stored) {
    try {
      const { token } = JSON.parse(stored);
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch {
      // Invalid localStorage data — skip auth header
    }
  }
  return config;
});

// Handle responses: log errors in dev, redirect to login on 401
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Kiosk mode has no way to sign in. Let the error propagate to react-query
      // so the existing stale indicator surfaces it; never redirect to /login.
      if (isKioskMode()) {
        return Promise.reject(error);
      }
      // Token expired or invalid — clear session and redirect to login
      localStorage.removeItem('auth_user');
      window.location.href = '/login';
      return new Promise(() => {}); // Page is navigating — suppress downstream handlers
    }
    if (ENV.isDevelopment) {
      console.error('API Error:', error.response?.data || error.message);
    }
    return Promise.reject(error);
  }
);

// API Types
export interface Organization {
  id: number;
  name: string;
  short_name: string;
  primary_contact_name?: string;
  primary_contact_phone?: string;
  primary_contact_email?: string;
  created_at?: string;
  updated_at?: string;
  total_locations?: number;
  active_locations?: number;
  inactive_locations?: number;
}

/** CRM manager row, stored verbatim from the org-units lookup wire shape
 * (sc-6111). Deliberately un-ranked and un-mapped on the Edge side — display
 * is for cross-referencing against the CRM, nothing more. */
export interface MeshManager {
  contact_id: string;
  name: string;
  email: string | null;
  contact_type: string;
}

// Org-scoped group of interoperating branch locations that share inventory
// (sc-6059). A location belongs to at most one mesh, and the mesh's
// organization must match the location's. Read-only mirror of CRM districts
// since sc-6111 — the nightly sync is the only writer.
export interface Mesh {
  id: number;
  name: string;
  organization_id: number;
  organization?: Pick<Organization, 'id' | 'name'>;
  /** Member locations, riding along on the index payload for the stacked
   * Locations column. */
  locations?: Array<{ id: number; name: string; active: boolean }>;
  /** CRM org_units.id (UUID). Null only on pre-flip hand-entered rows.
   * Like MeshManager's extra fields: verbatim wire shape, unread by this app
   * today — cross-referencing and future consumers (sc-6112). */
  crm_district_id?: string | null;
  crm_region_id?: string | null;
  region_name?: string | null;
  managers?: MeshManager[];
  synced_at?: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface DigestPreferences {
  restock: boolean;
  daily_usage: boolean;
  oos: boolean;
  rma: boolean;
  account_manager_name?: string;
  account_manager_email?: string;
  account_manager_title?: string;
  account_manager_phone?: string;
}

export interface Device {
  id: number;
  device_id: string;
  location_id: number | null;
  label?: string | null;
  power_configuration: 'Unknown' | 'Mains-Power' | 'Solar';
  staging: boolean;
  location?: Location;
  created_at?: string;
  updated_at?: string;
}

export interface Location {
  id: number;
  slug: string;
  name: string;
  timezone: string;
  active: boolean;
  alerts_enabled: boolean;
  /**
   * HEXA-internal facility (warehouse/tagging site), sc-5719. Suppresses
   * expected-state concerns and excludes the location from Edge Reviews and
   * digest sending. Mutually exclusive with hexahq_account_id (server-validated).
   */
  internal?: boolean;
  description?: string;
  organization_id: number;
  organization?: Organization;
  mesh_id?: number | null;
  /** Mesh name, riding along on location payloads for the list column. */
  mesh_name?: string | null;
  devices?: Device[];
  digest_recipient_email?: string;
  digest_recipient_name?: string;
  digest_preferences?: DigestPreferences;
  hexahq_account_id?: number | null;
  hexahq_account_name?: string | null;
  digest_restock_day_of_week?: number | null; // 0-6, where 0=Sunday, 6=Saturday
  quote_confirmation_deadline?: string | null;
  /** sc-6506: show quote_confirmation_deadline in the digest reply line. Off by default. */
  show_reply_by_time?: boolean;
  collect_account_info?: string | null;
  uses_stock_truck?: boolean;
  stock_truck_delivery_day?: number | null; // 0-6, where 0=Sunday, 6=Saturday
  ground_transit_business_days?: number | null;
  /** Restock digest cadence: send every N weeks (>= 1). 1 = every week. Form state may hold null while the input is cleared. */
  restock_interval_weeks?: number | null;
  /**
   * ISO date anchoring the every-N-weeks cadence: the Monday-based week containing
   * this date is a send week. Required when the interval is > 1; dormant
   * (preserved but ignored) at interval 1.
   */
  restock_anchor_date?: string | null;
  freight_option?: string | null;
  stock_truck_route?: string | null;
  /**
   * When true, the 4 AM digest generation job schedules `EdgeAccountDigestAutoSendJob`
   * at `digest_send_time` to run the full approval pipeline (refresh → gate →
   * quote creation → send) for this location's digest. AMs can still cancel
   * the auto-send from the digest preview while it's draft.
   */
  auto_send_enabled?: boolean;
  /** Auto-send time of day in HH:MM (24-hour) in the location's timezone. Defaults to '07:00'. */
  digest_send_time?: string | null;
  created_at?: string;
  updated_at?: string;
  /**
   * Onboarding lifecycle from the Outcomes registry, pushed by the Edge Onboard
   * app. Present on the single-location (`show`) payload only; null when no
   * registry row exists yet for the location.
   */
  onboarding?: LocationOnboarding | null;
  /**
   * Flat onboarding state on the locations LIST (`index`) payload — just the
   * state for the list column; null when the location has no registry row.
   */
  onboarding_state?: LocationOnboarding['state'];
}

export interface LocationOnboarding {
  state: 'not_started' | 'in_progress' | 'complete' | null;
  first_login_at: string | null;
  started_at: string | null;
  completed_at: string | null;
  synced_at: string | null;
}

// Extended type for location creation — device_id and power_configuration
// are accepted in the API payload but routed to Device creation, not stored on Location.
export interface CreateLocationData extends Partial<Location> {
  // Optional — omit to create a deviceless location.
  device_id?: string;
  power_configuration?: 'Unknown' | 'Mains-Power' | 'Solar';
  assign_device_ids?: number[];
}

export interface Manufacturer {
  id: number;
  name: string;
  created_at?: string;
  updated_at?: string;
}

export interface Product {
  sku: string;
  name: string;
  manufacturer_id?: number;
  manufacturer?: Manufacturer;
  product_equivalency?: ProductEquivalency;
  product_equivalency_category_id?: number;
  reorder_threshold: number;
  unit_cost: number | null;
  units_per_box: number | null;
  created_at?: string;
  updated_at?: string;
}

export interface BeaconAssignment {
  beacon_id: string;
  sku: string;
  status: 'active' | 'inactive' | 'retired';
  location_id?: number | null;
  assigned_at?: string;
  assigned_by?: string;
  notes?: string;
  created_at?: string;
  updated_at?: string;
  location?: Location;
}

export interface LocationAssignment {
  id: number;
  location_id: number;
  sku: string;
  assigned_at: string;
  assigned_by?: string;
  status: 'active' | 'inactive' | 'discontinued';
  expected_count?: number;
  reorder_threshold?: number;
  notes?: string;
  created_at: string;
  updated_at: string;
  location?: Location;
  product?: Product;
}

export interface CreateAssignmentData {
  location_id: number;
  sku: string;
  expected_count?: number;
  reorder_threshold?: number;
  assigned_by?: string;
  notes?: string;
}

export interface AssignmentFilters {
  location_id?: string | number; // Support both string (slug/device_id) and number (id)
  sku?: string;
  status?: string;
  organization_id?: string | number;
}

export interface EdgeAccountDigest {
  id: number;
  location_id: number;
  digest_date: string;
  status: 'draft' | 'approved' | 'pending_send' | 'sent' | 'cancelled';
  scheduled_send_at?: string;
  cancelled_at?: string;
  cancelled_by?: string;
  // Post-sc-5551 group shape (legacy rows are normalized server-side). sc-5986
  // grain context: max_count/total_units are UNITS, member qty_on_hand and
  // reorder_quantity are boxes (of the hexa_hq_sku) for boxed groups; the new
  // keys are absent on pre-sc-5986 payloads. No UI renders these numbers today
  // (the preview shows the backend-frozen email HTML) — typed for fidelity.
  restock_data: Array<{
    hexa_hq_sku: string | null;
    group_name: string;
    max_count: number;
    current_on_hand: number;
    total_units?: number;
    boxed?: boolean;
    hq_units_per_box?: number | null;
    reorder_quantity: number;
    members?: Array<{
      sku: string;
      qty_on_hand: number;
      units_per_box?: number | null;
      hide_when_zero?: boolean;
    }>;
  }>;
  daily_usage_data: Array<{
    sku: string;
    product_name: string;
    quantity_used: number;
    units_per_box?: number | null;
    units_used?: number;
    tag_ids: string[];
    is_oos: boolean;
  }>;
  oos_data: Array<{
    hexa_hq_sku: string;
    group_name: string;
    max_count: number;
    current_on_hand: number;
    boxed?: boolean;
    members: Array<{
      sku: string;
      qty_on_hand: number;
      units_per_box?: number | null;
    }>;
  }>;
  rma_data: any[]; // Placeholder for future implementation
  email_subject?: string;
  email_html_body?: string;
  email_text_body?: string;
  recipient_email?: string;
  approved_by?: string;
  approved_at?: string;
  sent_at?: string;
  created_at?: string;
  updated_at?: string;
  hexahq_quote_id?: number;
  quote_creation_status?: string;
  quote_creation_error?: string;
  hexahq_quote_url?: string;
  inventory_data_timestamp?: string;
  /**
   * Set by the show endpoint after `refresh_live_data!` runs. `'refreshed'`
   * means data is current; `'skipped_outage'` means refresh was bypassed
   * because a covering device is in an active DeviceOutage (so the data
   * shown is whatever was last successfully captured).
   */
  refresh_status?: string;
  /**
   * Surfaced by the index endpoint: true when any operational device at this
   * digest's location has an open DeviceOutage. Approval will be refused by
   * the gate in this state. Drives the row-level outage indicator on the
   * list view so reps see the refusal coming before opening the preview.
   */
  has_active_outage?: boolean;
  /**
   * Set by EdgeAccountDigestAutoSendJob (sc-5790) when it attempts the send-time
   * approval pipeline. When `auto_send_failure_reason` is present on a draft
   * digest, the auto-send pipeline ran and refused — the digest stays in
   * draft for manual approval.
   */
  auto_send_attempted_at?: string;
  auto_send_failure_reason?: string;
  auto_send_failure_message?: string;
  location?: Location;
  sections_included?: string[];
  /**
   * True when the digest has been soft-deleted (archived). Only populated by
   * the index endpoint when `include_archived` is requested; the UI badges
   * these rows and excludes them from the default view.
   */
  archived?: boolean;
  /**
   * Email-open engagement, surfaced by the index endpoint from HQ (via the
   * HexaOms facade). `email_opened` is true when the digest email was opened at
   * least once; `email_open_count` is the total number of opens. Both default
   * to false/0 for unsent digests or when the HQ source is unavailable.
   */
  email_opened?: boolean;
  email_open_count?: number;
  /**
   * Email delivery status mirrored from HQ's email hub. `'failed'` means HQ
   * accepted the send but delivery bounced — the digest reads `sent` yet never
   * reached the customer. Drives the `bounced` blocked bucket on the attention
   * endpoint (sc-5827).
   */
  email_delivery_status?: string;
  email_delivered_at?: string;
  /**
   * Only set on rows returned in the `blocked` bucket of the attention endpoint
   * (sc-5827): `'outage'` (location in an active DeviceOutage — can't send good
   * data yet), `'bounced'` (delivery failed), or `'cancelled'` (cancelled
   * digest — suggest Archive). null for actionable / manual rows.
   */
  blocked_reason?: 'outage' | 'bounced' | 'cancelled' | null;
}

/**
 * Morning-triage buckets for the digest reports page (sc-5827), split by
 * current actionability. Returned by GET /edge_account_digests/attention.
 */
export interface DigestAttention {
  action_required: EdgeAccountDigest[];
  blocked: EdgeAccountDigest[];
  manual_drafts: EdgeAccountDigest[];
  /** Drafts queued to auto-send (scheduled_send_at set, no failure yet). */
  scheduled: EdgeAccountDigest[];
  meta: {
    cap: number;
    counts: { action_required: number; blocked: number; manual_drafts: number; scheduled: number };
    truncated: { action_required: boolean; blocked: boolean; manual_drafts: boolean; scheduled: boolean };
  };
}

/** A digest type — the derived label that picks a digest's subject (sc-6504). */
export type DigestSettingsType = 'restock' | 'oos' | 'all_stocked' | 'usage';

export interface DigestTypeSetting {
  enabled: boolean;
  /** Subject template with `{{token}}` placeholders. */
  subject: string;
}

/** GET/PATCH /edge_account_digests/settings — global subject templates + toggles. */
export interface DigestSettings {
  types: Record<DigestSettingsType, DigestTypeSetting & { default_subject: string }>;
  tokens: string[];
  updated_by_email: string | null;
  updated_at: string | null;
}

/** PATCH body: full replace — all four types required. */
export interface DigestSettingsUpdate {
  types: Record<DigestSettingsType, DigestTypeSetting>;
}

export interface DigestFilters {
  status?: 'draft' | 'approved' | 'pending_send' | 'sent' | 'cancelled';
  location_id?: number;
  start_date?: string;
  end_date?: string;
  section?: string;
  /** Include soft-deleted (archived) digests in the result. */
  include_archived?: boolean;
  /**
   * When true, the index drops rows already surfaced in the attention zones
   * (action-required / blocked / manual drafts) so the default table doesn't
   * double-list them (sc-5827). The page sends this only on its unfiltered view.
   */
  exclude_attention?: boolean;
  /**
   * When true, the index returns only digests created in today's generation run
   * (filtered on created_at, not digest_date — the digest covers yesterday).
   */
  created_today?: boolean;
}

// Product Equivalency Category types
export interface ProductEquivalencyCategory {
  id: number;
  name: string;
  created_at?: string;
  updated_at?: string;
}

// Product Equivalency types
export interface ProductEquivalency {
  id: number;
  name: string;
  hexa_hq_sku: string | null;
  hexahq_product_name?: string | null;
  product_equivalency_category_id: number;
  product_equivalency_category?: ProductEquivalencyCategory;
  description?: string;
  product_equivalency_memberships?: ProductEquivalencyMembership[];
  created_at?: string;
  updated_at?: string;
}

export interface ProductEquivalencyMembership {
  id: number;
  product_equivalency_id: number;
  sku: string;
  hide_when_zero?: boolean;
  product?: Product;
  product_equivalency?: ProductEquivalency;
  created_at?: string;
  updated_at?: string;
}

export type DeviceFreshness = 'fresh' | 'stale' | 'offline' | 'never';

export interface DeviceDiagnosticSnapshot {
  ts: string;
  seconds_ago: number;
  charge_percentage: number;
  battery_state: string;
  power_source: string;
  vin_volts: number;
}

export interface LocationDevice {
  id: number;
  device_id: string;
  label: string | null;
  power_configuration: 'Unknown' | 'Mains-Power' | 'Solar';
  created_at: string | null;
  updated_at: string | null;
  latest_diagnostic: DeviceDiagnosticSnapshot | null;
  freshness: DeviceFreshness;
}

export interface LocationDevicesResponse {
  devices: LocationDevice[];
}

// Order Fulfillment Visibility (sc-5858) — orders vs on-hand per order.
export type BeaconLedgerState = 'PRESENT' | 'DEPARTING' | 'ABSENT' | 'UNKNOWN';

export interface FulfillmentBeacon {
  beacon_id: string;
  arrived_at?: string | null;
  last_seen_at?: string | null;
  state: BeaconLedgerState;
  on_site: boolean;
  on_hand?: boolean;     // counts toward on-hand (PRESENT or soft-present DEPARTING)
  soft_present?: boolean; // DEPARTING but device silent — still here, gateway quiet; counted
  sku_mismatch?: boolean;
  present_location?: { id: number; name: string | null };
}

export interface FulfillmentSku {
  sku: string;
  ordered: number;
  tagged: number;
  on_hand: number;
  tracked: boolean;
  exception: boolean;     // on_hand < tagged — a tag that was placed isn't on hand now
  soft_present: boolean;  // on-hand count includes device-silent (soft-present) tags
  beacons: FulfillmentBeacon[];
}

export interface FulfillmentOrder {
  hexahq_order_id: number;
  po_number: string | null;
  status: string | null;
  ordered_at: string | null;
  skus: FulfillmentSku[];
}

export interface FulfillmentResidualSku {
  sku: string;
  on_hand: number;
  beacons: FulfillmentBeacon[];
}

export interface LocationOrderFulfillmentResponse {
  account_linked: boolean;
  hexahq_account_id?: number | null;
  degraded: boolean;
  truncated: boolean;
  window_days: number;
  as_of: string;
  orders: FulfillmentOrder[];
  residual: {
    unattributed: FulfillmentResidualSku[];
    out_of_window: FulfillmentResidualSku[];
  };
}

// sc-5864 — HVA Rate header card. account_linked/available gate the "—" state;
// rate_per_month is null below the 30d maturity floor (shown as "—/mo").
export interface HvaConversion {
  quote_id: number;
  quote_at: string | null;
  order_id: number;
  order_at: string | null;
}

export interface HvaSparkBucket {
  week_start: string;
  count: number;
}

export interface LocationHvaRateResponse {
  account_linked: boolean;
  available: boolean;
  mature: boolean;
  go_live_at: string | null;
  window_days: number;
  hva_count: number;
  rate_per_month: number | null;
  spark: HvaSparkBucket[];
  conversions: HvaConversion[];
}

// sc-5864 — Emails Opened header card. by_combination partitions the OPENED
// digests by their exact section combination; opened values sum to `opened`.
export interface EmailOpenCombination {
  sections: string[];
  label: string;
  opened: number;
}

export interface LocationEmailOpensResponse {
  account_linked: boolean;
  available: boolean;
  since: string | null;
  sent: number;
  opened: number;
  by_combination: EmailOpenCombination[];
}

export type OpenQuoteStatus = 'draft' | 'validated';

export interface OpenQuoteLine {
  sku: string;
  description: string | null;
  quantity: number;
  unit_price_cents: number | null;
}

export interface OpenQuote {
  id: number;
  order_number: string | null;
  status: OpenQuoteStatus;
  customer_name: string | null;
  requested_delivery_date: string | null;
  source: string;
  source_reference: string | null;
  hexahq_quote_id: number | string | null;
  hexahq_quote_url: string | null;
  created_at: string | null;
  line_count: number;
  lines: OpenQuoteLine[];
}

export interface LocationOpenQuotesResponse {
  quotes: OpenQuote[];
}

// Report-page engagement over a rolling 30-day window (sc-5866). When
// `available` is false (PostHog not configured, or the sync is stale > 48h) the
// counts are null and the card renders "—" rather than a misleading 0.
export interface LocationReportViews {
  available: boolean;
  since: string | null;
  visitors: number | null;
  sessions: number | null;
  pageviews: number | null;
}

export type InventoryStatus = 'oos' | 'restock' | 'healthy';

export interface InventoryMember {
  sku: string;
  product_name: string;
  count: number;
  units_per_box: number | null;
  expected_count: number | null;
  reorder_threshold: number | null;
}

export interface InventoryPeGroup {
  pe_id: number;
  name: string;
  category: string | null;
  hexa_hq_sku: string | null;
  max_count: number | null;
  total_on_hand: number;
  /** Σ(member count × units_per_box); equals total_on_hand for non-boxed groups. max_count is denominated in these units (sc-5986). */
  total_units: number;
  status: InventoryStatus;
  members: InventoryMember[];
}

export interface InventoryUngroupedItem {
  sku: string;
  product_name: string;
  count: number;
  expected_count: number | null;
  reorder_threshold: number | null;
  max_count: number | null;
  status: InventoryStatus;
}

export interface CustomerLocationInventory {
  /** Never sent for customer locations — the discriminant that narrows the union (sc-6110). */
  internal?: undefined;
  summary: {
    total_on_hand: number;
    oos_count: number;
    restock_count: number;
    healthy_count: number;
    pe_group_count: number;
    ungrouped_count: number;
  };
  pe_groups: InventoryPeGroup[];
  ungrouped: InventoryUngroupedItem[];
}

/**
 * Warehouse inventory row (sc-6110) — (sku × assigned_location) grain, one
 * grain in both group-by states. assigned_location_* is null for tags with no
 * active destination assignment (rendered "Unassigned"); a SKU staged for two
 * destinations is two rows. units_present = boxes_present × units_per_box
 * (NULL units_per_box reads 1), so units_present ≥ boxes_present always.
 */
export interface WarehouseInventoryRow {
  sku: string;
  product_name: string | null;
  /** Catalog-level equivalency name (membership by SKU); null when ungrouped. Display label only — no LPE, no comparator. */
  pe_group: string | null;
  assigned_location_id: number | null;
  assigned_location_name: string | null;
  boxes_present: number;
  units_present: number;
}

/** Discriminated payload for internal locations (sc-6110): counts only — no summary, no max/oos/restock semantics. */
export interface InternalLocationInventory {
  internal: true;
  rows: WarehouseInventoryRow[];
}

export type LocationInventory = CustomerLocationInventory | InternalLocationInventory;

export interface LocationProductEquivalency {
  id: number;
  location_id: number;
  product_equivalency_id: number;
  max_count?: number | null;
  // Per-location display order of this group within its category on the
  // customer report / dashboard (sc-6000). null => unpositioned, sorts
  // alphabetically after positioned groups.
  position?: number | null;
  status: 'active' | 'inactive';
  notes?: string;
  member_visibility_overrides?: Record<string, { hide_when_zero: boolean }>;
  location?: Pick<Location, 'id' | 'name' | 'slug'>;
  product_equivalency?: ProductEquivalency;
  created_at?: string;
  updated_at?: string;
}

// Device signal view (per-device install-QA page)
export type DeviceSignalRowKind = 'local' | 'misassigned' | 'unassigned_location' | 'other';
export type DeviceSignalPointAssignment = 'assigned_here' | 'misassigned' | 'unassigned_location' | 'no_assignment';

export interface DeviceSignalPoint {
  beacon_id: string;
  ts: string;
  rssi: number;
  assignment: DeviceSignalPointAssignment;
  assigned_location?: { id: number; name: string };
}

export interface DeviceSignalRow {
  sku: string | null;
  kind: DeviceSignalRowKind;
  roster_count: number | null;
  seen_count: number;
  seen_elsewhere_count: number | null;
  not_seen_count: number | null;
  points: DeviceSignalPoint[];
}

export interface DeviceSignalView {
  device: {
    id: number;
    device_id: string;
    label: string | null;
    power_configuration: 'Unknown' | 'Mains-Power' | 'Solar';
    staging: boolean;
    location: { id: number; name: string; slug: string } | null;
  };
  last_publish_at: string | null;
  silent_long_term: boolean;
  rssi_tally: { happy: number; weak: number; bad: number };
  rssi_zone_thresholds: { happy_min: number; weak_min: number };
  missing_total: number;
  location_device_count: number;
  rows: DeviceSignalRow[];
}

// Fleet dashboard types

// `publishes_24h` entry — array-encoded for payload compactness.
// [iso_ts, beacons_seen, beacons_good]
export type FleetPublishEntry = [string, number, number];

// `events_24h` entry — array-encoded for payload compactness.
// [iso_ts, transition_type]
export type FleetEventTransitionType = 'arrived' | 'departed' | 'reacquired' | 'false_departure';
export type FleetEventEntry = [string, FleetEventTransitionType];

export interface FleetLocation {
  id: number;
  name: string;
  slug: string;
  device_ids: number[];
  events_24h: FleetEventEntry[];
}

export interface FleetDevice {
  id: number;
  coreid: string;
  label: string | null;
  location_id: number | null;
  location: { id: number; name: string; slug: string } | null;
  staging: boolean;
  power_configuration: 'Unknown' | 'Mains-Power' | 'Solar';
  created_at: string | null;
  last_seen_at: string | null;
  last_diagnostic_at: string | null;
  vin_volts: number | null;
  charge_percentage: number | null;
  battery_state: string | null;
  good_beacons: number;
  total_beacons: number;
  publishes_24h: FleetPublishEntry[];
  comments_count: number;
}

// Active third-party platform incident (sc-6044) — surfaced as a fleet-page
// banner. Populated from the Edge-owned platform_incidents table (Particle
// Statuspage poll); empty when no watched incident is active.
export interface ActivePlatformIncident {
  provider: string;
  name: string;
  impact: string | null;
  status: string | null;
  components: string[];
  url: string | null;
  started_at: string | null;
}

export interface FleetDashboardPayload {
  as_of: string;
  locations: FleetLocation[];
  devices: FleetDevice[];
  staging_devices: FleetDevice[];
  needs_attention_devices: FleetDevice[];
  /** Active platform incidents (sc-6044). May be absent on older backends. */
  active_platform_incidents?: ActivePlatformIncident[];
}

// Internal Ops dashboard types (sc-5800) — four executive metric groups.

export interface AcquisitionWeekPoint {
  week: string;       // ISO date (Monday) of the bucket
  cumulative: number; // running total of active deployment locations
  net_new: number;    // locations that became active this week
}

// One period-anchored chart (sc-5932). The goal line runs from
// (start_date, start_count) to (end_date, target); the FE windows the shared
// `series` to [start_date, end_date] and draws.
export interface AcquisitionPeriod {
  key: 'quarter' | 'year';
  label: string;
  start_date: string;   // ISO date — goal-line anchor
  start_count: number;  // exact active locations created on/before start_date
  end_date: string;     // ISO date — target date
  target: number;       // shipped default 350 (quarter) / 600 (year)
}

export interface AcquisitionMetrics {
  series: AcquisitionWeekPoint[];
  current_count: number;
  periods: AcquisitionPeriod[]; // [quarter, year]
}

// Per-request Acquisition goal overrides, forwarded verbatim from the page URL
// (?acq_quarterly_target=400&acq_quarterly_end=2026-10-15&…). Targets are
// whole numbers, ends are ISO dates; each period's START is derived from its
// end server-side. The backend validates and silently falls back to the
// shipped goals, so anything it rejects renders as the default.
export type AcquisitionGoalParam =
  | 'acq_quarterly_target'
  | 'acq_quarterly_end'
  | 'acq_yearly_target'
  | 'acq_yearly_end';

export type AcquisitionGoalOverrides = Partial<Record<AcquisitionGoalParam, string>>;

export const ACQUISITION_GOAL_PARAMS: readonly AcquisitionGoalParam[] = [
  'acq_quarterly_target',
  'acq_quarterly_end',
  'acq_yearly_target',
  'acq_yearly_end',
];

export interface AdoptionMetrics {
  median_days_to_first_hva: number | null;   // median days go-live -> first Edge HVA; null if none
  avg_days_to_first_hva: number | null;      // mean of the same (dragged by the late-adopter tail)
  hva_count: number;                          // locations with >=1 Edge HVA
  // Equal two-week bins of mature locations' time to first HVA (labels in
  // weeks, last bin open-ended). The FE appends zero_hva_count as a "none"
  // bar, so distribution + none = mature_count.
  distribution: { label: string; count: number }[];
  zero_hva_pct: number | null;                // % of mature go-lives with no Edge HVA; null if none mature
  zero_hva_count: number;                     // mature go-lives with no Edge HVA
  mature_count: number;                       // go-lives live >= maturity_days (zero-HVA denominator)
  maturity_days: number;                      // 30
  proxy: boolean;
}

// Engagement panel — three views selected by ?engagement=coverage|funnel|volume,
// discriminated on `mode`. All over the live-deployment denominator, on the
// canonical Edge HVA (see webhook-server HvaMatcher).
export interface EngagementSegment {
  n: number;
  pct: number | null;
}

// coverage (default): HVA Active — % of live deployments with >= 1 HVA in the last 30 days.
export interface CoverageEngagement {
  mode: 'coverage';
  hva_active_pct: number | null;
  numerator: number;
  denominator: number;
  target: number; // 80
  proxy: boolean;
}

// funnel: mutually-exclusive buckets summing to the denominator, plus any-purchase and Edge-identified demand.
export interface FunnelEngagement {
  mode: 'funnel';
  denominator: number;
  converted: EngagementSegment;
  influenced: EngagementSegment;
  independent: EngagementSegment;
  dormant: EngagementSegment;
  any_purchase: EngagementSegment;
  edge_identified_pct: number | null; // converted + influenced
  target: number; // 80
  proxy: boolean;
}

// volume: HVA throughput over the window.
export interface VolumeEngagement {
  mode: 'volume';
  conversions: number;
  accounts: number;
  accounts_pct: number | null;
  denominator: number;
  revenue_usd: number;
  avg_transaction_usd: number | null;
  window: { start: string; end: string };
  target: number; // 80
  proxy: boolean;
}

export type EngagementMetrics = CoverageEngagement | FunnelEngagement | VolumeEngagement;

export interface NoiseMetrics {
  noise_pct: number | null; // noise rate: (restock prompts − converted) / restock prompts; lower is better
  numerator: number; // emails that converted to an order (Edge HVA)
  denominator: number; // all emails sent in the window
  actionable_count: number; // sent restock (purchase-prompt) emails in the window
  conversions_available: boolean; // false when the HQ replica couldn't be read
  target: number; // 0 (lower is better)
  proxy: boolean;
}

// Hardware panel (sc-6187). Both halves are LOCATION-grained rows under DEVICE
// counts: the hero numbers are device counts so Recurring Offenders keeps
// matching what Edge posts to the CRM / Ninety, while the lists roll up to one
// row per site because device IDs are Particle hardware IDs nobody recognizes.
// `more` therefore counts remaining LOCATIONS, so 5 + more !== count whenever a
// location has several qualifying devices.
/**
 * Same vocabulary as the CRM's Hardware Investigations StatusDot — but NOT the
 * same predicate. The CRM's dot follows `reported_at` (stamped only when Edge's
 * outbound push succeeds); this one follows outage AGE against the same
 * threshold that would have triggered the push, so it can't be turned green by a
 * broken integration. Expect the two to differ for an outage between ~20min
 * (watchdog) and ~2h (reportable). "outage" = open outage old enough to be
 * actionable, "online" = heard from at some point, "unknown" = never heard from.
 */
export type HardwareConnectivityStatus = 'outage' | 'online' | 'unknown';

export interface HardwareRecurringRow {
  /** Edge location id — the row's identity (locations.name has no DB uniqueness constraint). */
  location_id: number;
  location_name: string;
  /** Worst days-silent across the location's qualifying devices, last complete week. */
  days_silent: number;
  /** Consecutive qualifying weeks back from the last complete week, for this
   * device AT THIS LOCATION; capped at `streak_cap`. */
  streak: number;
  /**
   * LIVE status, worst-of across the location's qualifying devices
   * (outage > unknown > online). Qualifying last week does not mean down now —
   * without this the list implies every site on it is currently dark.
   */
  status: HardwareConnectivityStatus;
}

export interface HardwareOfflineRow {
  location_id: number;
  location_name: string;
  /** Worst open-outage age across the location's devices, in days. */
  days_offline: number;
}

export interface HardwareMetrics {
  /** False when the section failed to compute; the board shows "—", not a zero. */
  available?: boolean;
  recurring: {
    count: number; // DEVICES qualifying in the last complete week
    /** Trailing weeks the backend scanned. A streak at the cap is "at least N". */
    streak_cap: number;
    week_start: string; // YYYY-MM-DD — carried for debugging; not rendered
    week_end: string;
    top: HardwareRecurringRow[]; // <= 5
    more: number; // LOCATIONS beyond the 5 shown
  };
  offline_24h: {
    /** DEVICES currently dark >= threshold_hours. MAY OVERLAP `recurring` — the
     * two halves answer independent questions, so the counts are not summable. */
    count: number;
    threshold_hours: number; // mirrors the CRM's AGING_OUTAGE_HOURS
    top: HardwareOfflineRow[]; // <= 5
    more: number;
  };
}

export interface InternalOpsDashboardPayload {
  as_of: string;
  acquisition: AcquisitionMetrics;
  adoption: AdoptionMetrics;
  engagement: EngagementMetrics;
  noise: NoiseMetrics;
  /** Optional on purpose: a webhook-server rollback or a stale task mid-rollout
   * can serve a payload without it, and tags-admin has no ErrorBoundary — an
   * unguarded dereference unmounts the whole board with no recovery on the poll.
   * Declaring it optional makes the render-site guard compile-time enforced. */
  hardware?: HardwareMetrics;
}

export interface DeviceComment {
  ts: string;
  author: string;
  body: string;
}

export interface DeviceCommentsResponse {
  comments: DeviceComment[];
}

// Pagination types
export interface PaginationParams {
  page?: number;
  per_page?: number;
  sort_by?: string;
  sort_dir?: 'asc' | 'desc';
  filter_field?: string;
  filter_op?: string;
  filter_value?: string;
}

export interface PaginationMeta {
  page: number;
  per_page: number;
  total_count: number;
  has_more: boolean;
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: PaginationMeta;
} 
// ============================================================================
// Edge Onboard — admin API types (webhook-server engines/edge_onboard)
// Contract: engines/edge_onboard/docs/openapi.yaml (admin-* tags)
// ============================================================================

export type EdgeOnboardGoLiveState = 'Not Started' | 'In Progress' | 'Complete';
export type EdgeOnboardReviewState = 'Not Started' | 'Under Review' | 'Complete';
export type EdgeOnboardSlipState = 'Available' | 'Pending' | 'Assigned';
export type EdgeOnboardSubmissionReviewState = 'Not Reviewed' | 'Reviewed';

export interface EdgeOnboardCustomerUser {
  id: number;
  /** Legacy Replit identity — null on accounts created in-app. */
  username: string | null;
  first_name: string;
  /** Nullable — single-word names and legacy imports. */
  last_name: string | null;
  email: string | null;
  phone: string | null;
  location_id: number | null;
  must_change_password: boolean;
  password_changed_at: string | null;
  /** Set on soft-deleted users (only visible with include_deleted=true). */
  deleted_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface EdgeOnboardCustomerUserInput {
  password: string;
  first_name: string;
  /** Optional — single-word names are fine. */
  last_name?: string;
  /** The account identity — customers sign in with this. */
  email: string;
  phone?: string | null;
  location_id: number | null;
  must_change_password?: boolean;
}

export interface EdgeOnboardCustomerUserUpdate {
  first_name?: string;
  last_name?: string;
  email?: string | null;
  phone?: string | null;
  location_id?: number | null;
  must_change_password?: boolean;
  /** Setting a password is a reset: forces must_change_password and revokes sessions. */
  password?: string;
}

/** One @-mentioned Slack member on onboarding notifications (sc-6383). */
export interface EdgeOnboardSlackMention {
  /** Human label for the admin list only; not sent to Slack. */
  label: string;
  /** Slack member ID (`U…` / `W…`) — the only thing Slack webhooks can ping. */
  member_id: string;
}

/** Singleton engine settings: GET/PATCH /edge_onboard/admin/settings. */
export interface EdgeOnboardSettings {
  slack_mentions: EdgeOnboardSlackMention[];
  updated_by_email: string | null;
  updated_at: string | null;
}

export interface EdgeOnboardGoLiveCounts {
  submissions: number;
  tag_slips_total: number;
  tag_slips_available: number;
  tag_slips_pending: number;
  tag_slips_assigned: number;
}

export interface EdgeOnboardGoLive {
  id: number;
  location_id: number;
  customer_user_id: number;
  state: EdgeOnboardGoLiveState;
  review_state: EdgeOnboardReviewState;
  /** Present on admin index/show; absent on create/update/complete/reopen responses. */
  counts?: EdgeOnboardGoLiveCounts;
  created_at: string;
  updated_at: string;
}

export interface EdgeOnboardGoLiveDetail extends EdgeOnboardGoLive {
  location: { id: number; name: string; hexahq_account_id: number | null; internal?: boolean } | null;
  customer_user: { id: number; username: string | null; email: string | null; full_name: string } | null;
}

export interface EdgeOnboardSubmission {
  id: number;
  go_live_id: number;
  quantity: number;
  max_quantity: number | null;
  restocking: boolean;
  notes: string | null;
  review_state: EdgeOnboardSubmissionReviewState;
  reviewed_by_email: string | null;
  reviewed_at: string | null;
  /** Presigned GET (1h TTL, regenerated per fetch); null until a photo is attached. */
  photo_url: string | null;
  /** Derived from beacon_assignments via the submission's tag MACs; null until assigned. */
  assigned_product: { sku: string; name: string | null } | null;
  tag_slip_ids: number[];
  created_at: string;
  updated_at: string;
}

export interface EdgeOnboardTagSlip {
  id: number;
  go_live_id: number;
  submission_id: number | null;
  /** Printed sequential number, unique per go-live. */
  slip_id: number;
  mac_address: string;
  state: EdgeOnboardSlipState;
  pending_since: string | null;
  created_at: string;
}
